<!-- src/components/wages/WageSummaryContent.vue -->
<template>
  <div class="wage-summary-root">
    <div class="core-section-header">
      <div class="core-section-header-main">
        <h5 class="core-section-title">Weekly Wage Summary</h5>
      </div>

      <div v-if="canShowPrintButton" class="core-actions core-actions--right">
        <button
          type="button"
          class="btn purple darken-2"
          :disabled="printingPdf || !venueId || !weekEndingISO"
          @click.prevent="printWageSummaryPdf"
          title="Generate and open the wage summary PDF"
        >
          <template v-if="!printingPdf">
            <i class="material-icons"> print </i>

            <span> Print PDF </span>
          </template>

          <span v-else> Generating… </span>
        </button>
      </div>
    </div>

    <div v-if="loadingContract" class="core-feedback core-feedback--neutral">
      Loading managers contract…
    </div>

    <div v-if="loadingActualSales" class="core-feedback core-feedback--neutral">
      Loading actual sales…
    </div>

    <div v-if="loadingActualClockHours" class="core-feedback core-feedback--neutral">
      Loading clocked actual hours…
    </div>

    <div v-if="loadingForecast" class="core-feedback core-feedback--neutral">
      Loading forecast totals…
    </div>

    <div v-if="savingActualWages" class="core-feedback core-feedback--neutral">
      Saving actual hours…
    </div>

    <div v-if="error" class="core-feedback core-feedback--error">
      {{ error }}
    </div>

    <div class="core-section">
      <table class="highlight core-table">
        <tbody>
          <tr>
            <td>Total Hours</td>

            <td class="core-align-right">
              {{ weeklyHours.toFixed(1) }}
            </td>
          </tr>

          <tr>
            <td>Rota Wages</td>

            <td class="core-align-right">
              {{ formatCurrency(weeklyWagesFromRota) }}
            </td>
          </tr>

          <tr>
            <td>Actual Wages</td>

            <td class="core-align-right">
              {{ formatCurrency(actualWages) }}
            </td>
          </tr>

          <tr>
            <td>Actual Sales</td>

            <td class="core-align-right">
              {{ formatCurrency(weeklyActualSales) }}
            </td>
          </tr>

          <tr>
            <td>Forecast Sales</td>

            <td class="core-align-right">
              {{ formatCurrency(weeklyForecast) }}
            </td>
          </tr>

          <tr>
            <td>Contract Wages (£)</td>

            <td class="core-align-right">
              {{ formatCurrency(managersContract) }}
            </td>
          </tr>

          <tr>
            <td>Variance (£)</td>

            <td class="core-align-right">
              {{ formatSignedCurrency(varianceWage) }}
            </td>
          </tr>

          <tr>
            <td>Actual Wage %</td>

            <td class="core-align-right">{{ actualWagePct.toFixed(1) }}%</td>
          </tr>

          <tr>
            <td>Forecast Wage %</td>

            <td class="core-align-right">{{ forecastWagePct.toFixed(1) }}%</td>
          </tr>

          <tr>
            <td>Variance %</td>

            <td class="core-align-right">
              {{ formatSignedPercent(variancePct) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="core-section">
      <h6 class="core-section-title">Daily Summary</h6>

      <div class="core-table-wrap">
        <table class="highlight core-table core-table--nowrap">
          <thead>
            <tr>
              <th>Date</th>
              <th>Day</th>

              <th class="core-align-right">Hours</th>

              <th class="core-align-right">Wages £</th>

              <th class="core-align-right">Forecast Sales</th>

              <th class="core-align-right">Actual Sales</th>

              <th class="core-align-right">Forecast Wage %</th>

              <th class="core-align-right">Actual Wage %</th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="d in dailyRows" :key="d.ymd">
              <td>{{ d.ymd }}</td>

              <td class="core-strong">
                {{ d.label }}
              </td>

              <td class="core-align-right">
                {{ d.hours.toFixed(1) }}
              </td>

              <td class="core-align-right">
                {{ formatCurrency(d.wages) }}
              </td>

              <td class="core-align-right">
                {{ formatCurrency(d.forecast) }}
              </td>

              <td class="core-align-right">
                {{ formatCurrency(d.actualSales) }}
              </td>

              <td class="core-align-right">{{ d.forecastWagePct.toFixed(1) }}%</td>

              <td class="core-align-right">{{ d.actualWagePct.toFixed(1) }}%</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="core-section">
      <h6 class="core-section-title">Staff Weekly Totals</h6>

      <div
        v-if="
          staffWeeklyRows.length === 0 &&
          !loadingContract &&
          !loadingActualSales &&
          !loadingActualClockHours &&
          !loadingForecast
        "
        class="core-empty-state"
      >
        No staff available for this venue.
      </div>

      <div v-else class="core-table-wrap">
        <table class="highlight core-table core-table--nowrap">
          <thead>
            <tr>
              <th>Staff</th>
              <th>Job Title</th>

              <th class="core-align-right">Pay Rate</th>

              <th class="core-align-right">Rota Hours</th>

              <th class="core-align-right">Rota Wages £</th>

              <th class="core-align-right">
                <span class="actual-hours-header">
                  <span> Actual Hours </span>

                  <span class="hover-tooltip-wrap" aria-hidden="false">
                    <i class="material-icons tiny hover-tooltip-icon"> info_outline </i>

                    <span class="hover-tooltip-bubble">
                      Auto-filled from rounded clock-in and rounded clock-out times.
                      Manual overrides must be HH:mm in 15-minute blocks.
                    </span>
                  </span>
                </span>
              </th>

              <th class="core-align-right">Actual Wages £</th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="s in staffWeeklyRows" :key="s.staffId">
              <td class="core-strong">
                {{ s.name }}
              </td>

              <td>
                {{ s.jobTitle }}
              </td>

              <td class="core-align-right">
                {{ formatCurrency(s.payRate) }}
              </td>

              <td class="core-align-right">
                {{ s.hours.toFixed(1) }}
              </td>

              <td class="core-align-right">
                {{ formatCurrency(s.wages) }}
              </td>

              <td class="core-align-right">
                <div class="actual-hours-input-wrap">
                  <input
                    :value="actualHoursInputValue(s.staffId)"
                    type="text"
                    inputmode="numeric"
                    maxlength="6"
                    placeholder="00:00"
                    pattern="^[0-9]{1,3}:(00|15|30|45)$"
                    class="core-input actual-hours-input"
                    @input="onActualHoursInput(s.staffId, $event)"
                    @blur="onActualHoursBlur(s.staffId, $event)"
                  />
                </div>
              </td>

              <td class="core-align-right">
                {{ formatCurrency(actualWagesForStaff(s)) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div
        v-if="staffWeeklyRows.length > 0"
        class="core-actions core-actions--right"
        style="margin-top: 14px"
      >
        <button
          class="btn-small blue-grey darken-2"
          :disabled="savingActualWages || !canSaveActualWages"
          @click="saveActualWages"
        >
          Submit
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch, onMounted, onUnmounted } from "vue";
import { auth, db } from "@/firebase";

import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  where,
} from "firebase/firestore";

import { getFunctions, httpsCallable } from "firebase/functions";

import { getTillsForDateRange } from "@/scripts/sqlApiHelper/tillsApi";

/* global M */

const TIME_CLOCK_SUBCOLLECTION = "time_clock_shifts";

const VALID_HHMM_RE = /^(\d{1,3}):(00|15|30|45)$/;

const props = defineProps({
  venueId: {
    type: String,
    default: "",
  },

  weekDays: {
    type: Array,
    default: () => [],
  },

  weekRangeText: {
    type: String,
    default: "",
  },

  weekEndingDate: {
    type: String,
    default: "",
  },

  staffRows: {
    type: Array,
    default: () => [],
  },

  shiftsForCell: {
    type: Function,
    required: true,
  },

  isOperations: {
    type: Boolean,
    default: false,
  },
});

const loadingForecast = ref(false);

const loadingContract = ref(false);

const loadingActualSales = ref(false);

const loadingActualClockHours = ref(false);

const savingActualWages = ref(false);

const printingPdf = ref(false);

const error = ref("");

const forecastByDate = ref(new Map());

const weeklyForecast = ref(0);

const tillRows = ref([]);

const managersContract = ref(0);

/*
 * Manual input, saved input, and clocked input keyed by staffId.
 */
const actualHoursInputMap = ref({});

const actualHoursSavedMap = ref({});

const actualClockHoursMap = ref({});

/*
 * Sticky resolved values.
 */
const resolvedWeeklyHours = ref(0);

const resolvedWeeklyRotaWages = ref(0);

const resolvedDailyRows = ref([]);

const resolvedStaffWeeklyRows = ref([]);

const dashboardVisible = ref(false);

let weeklyPackUnsub = null;

/* =========================================================
   BASIC HELPERS
========================================================= */

function safeNum(v) {
  const n = Number(v);

  return Number.isFinite(n) ? n : 0;
}

function isISODate(s) {
  return /^\d{4}-\d{2}-\d{2}$/.test(String(s || ""));
}

function firstNumber(...vals) {
  for (const v of vals) {
    const n = Number(v);

    if (Number.isFinite(n)) {
      return n;
    }
  }

  return 0;
}

function asStr(v) {
  return String(v ?? "").trim();
}

function fns() {
  return getFunctions(undefined, "us-central1");
}

function extractUrl(data) {
  return data?.downloadUrl || data?.url || data?.pdfUrl || data?.signedUrl || "";
}

/* =========================================================
   FORMATTERS
========================================================= */

function formatCurrency(v) {
  return `£${safeNum(v).toFixed(2)}`;
}

function formatSignedCurrency(v) {
  const n = Number(v);

  const safe = Number.isFinite(n) ? n : 0;

  return `${safe < 0 ? "-" : ""}£${Math.abs(safe).toFixed(2)}`;
}

function formatSignedPercent(v) {
  const n = Number(v);

  const safe = Number.isFinite(n) ? n : 0;

  return `${safe < 0 ? "-" : ""}${Math.abs(safe).toFixed(1)}%`;
}

function showToast(html, classes = "") {
  try {
    if (typeof M !== "undefined" && M?.toast) {
      M.toast({
        html,
        classes,
      });
    }
  } catch (_) {
    // no-op
  }
}

/* =========================================================
   DATA FIELD HELPERS
========================================================= */

function readTradingDate(data = {}) {
  return String(
    data?.trading_date || data?.tradingDate || data?.date || data?.business_date || ""
  ).trim();
}

function staffIdOf(row) {
  return String(
    row?.id ?? row?.staff_id ?? row?.staffId ?? row?.uid ?? row?.user_id ?? ""
  ).trim();
}

function staffName(row) {
  return String(
    row?.staff_name ||
      row?.name ||
      row?.staffName ||
      row?.display_name ||
      row?.displayName ||
      "—"
  ).trim();
}

function staffJobTitle(row) {
  return String(
    row?.job_title || row?.jobTitle || row?.role || row?.position || "—"
  ).trim();
}

function staffPayRate(row) {
  return firstNumber(
    row?.pay_rate,
    row?.payRate,
    row?.hourly_rate,
    row?.hourlyRate,
    row?.rate,
    0
  );
}

/* =========================================================
   ROTA SHIFT HELPERS
========================================================= */

function extractShiftStart(shift) {
  return String(
    shift?.startTime ?? shift?.start_time ?? shift?.start ?? shift?.from ?? ""
  ).trim();
}

function extractShiftEnd(shift) {
  return String(
    shift?.endTime ?? shift?.end_time ?? shift?.end ?? shift?.to ?? ""
  ).trim();
}

function extractBreakMins(shift) {
  return firstNumber(
    shift?.unpaid_break_mins,
    shift?.unpaidBreakMins,
    shift?.break_mins,
    shift?.breakMins,
    shift?.break_minutes,
    shift?.breakMinutes,
    0
  );
}

function timeToMinutes(t) {
  const s = String(t || "")
    .trim()
    .toUpperCase();

  if (!s) {
    return null;
  }

  let m = s.match(/^(\d{1,2}):(\d{2})$/);

  if (m) {
    const hh = Number(m[1]);

    const mm = Number(m[2]);

    if (hh >= 0 && hh <= 23 && mm >= 0 && mm <= 59) {
      return hh * 60 + mm;
    }
  }

  m = s.match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)$/);

  if (m) {
    let hh = Number(m[1]);

    const mm = Number(m[2] || 0);

    const ap = m[3];

    if (!(hh >= 1 && hh <= 12) || !(mm >= 0 && mm <= 59)) {
      return null;
    }

    if (ap === "AM") {
      if (hh === 12) {
        hh = 0;
      }
    } else if (ap === "PM") {
      if (hh !== 12) {
        hh += 12;
      }
    }

    return hh * 60 + mm;
  }

  return null;
}

function shiftHoursFromObject(shift) {
  const explicitHours = firstNumber(
    shift?.paid_hours,
    shift?.paidHours,
    shift?.hours,
    shift?.shift_hours,
    shift?.shiftHours,
    shift?.duration_hours,
    shift?.durationHours
  );

  if (explicitHours > 0) {
    return explicitHours;
  }

  const startTime = extractShiftStart(shift);

  const endTime = extractShiftEnd(shift);

  const s = timeToMinutes(startTime);

  const e = timeToMinutes(endTime);

  if (s == null || e == null) {
    return 0;
  }

  let diff = e - s;

  if (diff < 0) {
    diff += 24 * 60;
  }

  diff -= extractBreakMins(shift);

  if (diff < 0) {
    diff = 0;
  }

  return diff / 60;
}

/* =========================================================
   DATE HELPERS
========================================================= */

function parseYmd(ymd) {
  const [y, m, d] = String(ymd)
    .split("-")
    .map((n) => Number(n));

  return new Date(Date.UTC(y, m - 1, d));
}

function toYmd(dateObj) {
  const y = dateObj.getUTCFullYear();

  const m = String(dateObj.getUTCMonth() + 1).padStart(2, "0");

  const d = String(dateObj.getUTCDate()).padStart(2, "0");

  return `${y}-${m}-${d}`;
}

function addDays(dateObj, n) {
  const d = new Date(dateObj.getTime());

  d.setUTCDate(d.getUTCDate() + n);

  return d;
}

/* =========================================================
   CLOCK HELPERS
========================================================= */

function timestampToDate(value) {
  if (!value) {
    return null;
  }

  if (typeof value.toDate === "function") {
    return value.toDate();
  }

  const d = new Date(value);

  return Number.isNaN(d.getTime()) ? null : d;
}

function timestampToMillis(value) {
  const d = timestampToDate(value);

  return d ? d.getTime() : null;
}

function roundMinutesToNearest15(minutes) {
  const n = Number(minutes);

  if (!Number.isFinite(n) || n <= 0) {
    return 0;
  }

  return Math.round(n / 15) * 15;
}

function roundTimestampToNearest15Millis(value) {
  const ms = timestampToMillis(value);

  if (ms == null) {
    return null;
  }

  const interval = 15 * 60 * 1000;

  return Math.round(ms / interval) * interval;
}

/* =========================================================
   ACTUAL HOURS FORMAT
========================================================= */

function decimalHoursToHHMM(value) {
  const n = Number(value);

  if (!Number.isFinite(n) || n < 0) {
    return "";
  }

  const roundedTotalMins = roundMinutesToNearest15(n * 60);

  const hours = Math.floor(roundedTotalMins / 60);

  const mins = roundedTotalMins % 60;

  return `${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}`;
}

function hhmmToDecimalHours(value) {
  const raw = String(value ?? "").trim();

  if (!raw) {
    return 0;
  }

  const match = raw.match(VALID_HHMM_RE);

  if (!match) {
    return 0;
  }

  const hours = Number(match[1]);

  const mins = Number(match[2]);

  if (!Number.isFinite(hours) || !Number.isFinite(mins)) {
    return 0;
  }

  return hours + mins / 60;
}

/* =========================================================
   ACTUAL HOURS INPUT
========================================================= */

function normalizeActualHoursInput(value, previousValue = "") {
  const raw = String(value ?? "").trim();

  if (!raw) {
    return "";
  }

  /*
   * Only allow digits and one optional colon while typing.
   */
  if (!/^\d{0,3}:?\d{0,2}$/.test(raw)) {
    return previousValue;
  }

  const parts = raw.split(":");

  if (parts.length > 2) {
    return previousValue;
  }

  const hours = parts[0] ?? "";

  const minutes = parts[1] ?? "";

  if (hours.length > 3) {
    return previousValue;
  }

  /*
   * Allow typing hours before the colon.
   */
  if (!raw.includes(":")) {
    return hours;
  }

  /*
   * Allow "12:" while the user is still typing.
   */
  if (minutes.length === 0) {
    return `${hours}:`;
  }

  /*
   * First minute digit must be able to become
   * 00, 15, 30, or 45.
   */
  if (minutes.length === 1) {
    if (!["0", "1", "3", "4"].includes(minutes)) {
      return previousValue;
    }

    return `${hours}:${minutes}`;
  }

  /*
   * Final allowed minute values only.
   */
  if (["00", "15", "30", "45"].includes(minutes)) {
    return `${hours}:${minutes}`;
  }

  return previousValue;
}

function normalizeActualHoursOnBlur(value) {
  const raw = String(value ?? "").trim();

  if (!raw) {
    return "";
  }

  const fullMatch = raw.match(VALID_HHMM_RE);

  if (fullMatch) {
    const hours = String(Number(fullMatch[1])).padStart(2, "0");

    return `${hours}:${fullMatch[2]}`;
  }

  const hourOnlyMatch = raw.match(/^(\d{1,3})$/);

  if (hourOnlyMatch) {
    const hours = String(Number(hourOnlyMatch[1])).padStart(2, "0");

    return `${hours}:00`;
  }

  const hourColonMatch = raw.match(/^(\d{1,3}):$/);

  if (hourColonMatch) {
    const hours = String(Number(hourColonMatch[1])).padStart(2, "0");

    return `${hours}:00`;
  }

  const partialMinuteMatch = raw.match(/^(\d{1,3}):([0134])$/);

  if (partialMinuteMatch) {
    const hours = String(Number(partialMinuteMatch[1])).padStart(2, "0");

    const minutesMap = {
      0: "00",
      1: "15",
      3: "30",
      4: "45",
    };

    return `${hours}:${minutesMap[partialMinuteMatch[2]]}`;
  }

  return "";
}

function sanitizeHoursInput(value, previousValue = "") {
  return normalizeActualHoursInput(value, previousValue);
}

function hasOwn(obj, key) {
  return Object.prototype.hasOwnProperty.call(obj || {}, key);
}

function actualHoursInputValue(staffId) {
  const sid = String(staffId || "").trim();

  if (hasOwn(actualHoursInputMap.value, sid)) {
    return actualHoursInputMap.value[sid] ?? "";
  }

  if (hasOwn(actualClockHoursMap.value, sid)) {
    return decimalHoursToHHMM(actualClockHoursMap.value[sid]);
  }

  if (hasOwn(actualHoursSavedMap.value, sid)) {
    return decimalHoursToHHMM(actualHoursSavedMap.value[sid]);
  }

  return "";
}

function actualHoursForStaffId(staffId) {
  const sid = String(staffId || "").trim();

  if (hasOwn(actualHoursInputMap.value, sid)) {
    return hhmmToDecimalHours(actualHoursInputMap.value?.[sid]);
  }

  if (hasOwn(actualClockHoursMap.value, sid)) {
    const clockNum = Number(actualClockHoursMap.value?.[sid]);

    if (Number.isFinite(clockNum) && clockNum >= 0) {
      return clockNum;
    }
  }

  if (hasOwn(actualHoursSavedMap.value, sid)) {
    const savedNum = Number(actualHoursSavedMap.value?.[sid]);

    if (Number.isFinite(savedNum) && savedNum >= 0) {
      return savedNum;
    }
  }

  return 0;
}

function actualWagesForStaff(staffRow) {
  return safeNum(staffRow?.payRate) * actualHoursForStaffId(staffRow?.staffId);
}

function onActualHoursInput(staffId, event) {
  const sid = String(staffId || "").trim();

  if (!sid) {
    return;
  }

  const currentValue = actualHoursInputValue(sid);

  const nextValue = sanitizeHoursInput(event?.target?.value, currentValue);

  actualHoursInputMap.value = {
    ...actualHoursInputMap.value,

    [sid]: nextValue,
  };

  if (event?.target) {
    event.target.value = nextValue;
  }
}

function onActualHoursBlur(staffId, event) {
  const sid = String(staffId || "").trim();

  if (!sid) {
    return;
  }

  const nextValue = normalizeActualHoursOnBlur(event?.target?.value);

  actualHoursInputMap.value = {
    ...actualHoursInputMap.value,

    [sid]: nextValue,
  };

  if (event?.target) {
    event.target.value = nextValue;
  }
}

/* =========================================================
   CLOCK SHIFT HELPERS
========================================================= */

function clockShiftStaffId(row = {}) {
  return String(
    row?.staffId ??
      row?.teamMemberId ??
      row?.authUid ??
      row?.staff_id ??
      row?.user_id ??
      row?.uid ??
      ""
  ).trim();
}

function clockShiftHours(row = {}) {
  const inMs = roundTimestampToNearest15Millis(row?.clockInRoundedAt);

  const outMs = roundTimestampToNearest15Millis(row?.clockOutRoundedAt);

  if (inMs == null || outMs == null || outMs <= inMs) {
    return 0;
  }

  const workedMins = Math.max(0, (outMs - inMs) / 60000);

  const roundedWorkedMins = roundMinutesToNearest15(workedMins);

  return roundedWorkedMins / 60;
}

/* =========================================================
   WEEKLY PACK STATE
========================================================= */

function attachWeeklyPackState(vid) {
  if (typeof weeklyPackUnsub === "function") {
    weeklyPackUnsub();
  }

  weeklyPackUnsub = null;

  dashboardVisible.value = false;

  const v = String(vid || "").trim();

  if (!v) {
    return;
  }

  weeklyPackUnsub = onSnapshot(
    doc(db, "venues", v, "weekly_pack", "state"),

    (snap) => {
      const data = snap.exists() ? snap.data() || {} : {};

      dashboardVisible.value = data.dashboardVisible === true;
    },

    (err) => {
      console.warn("[WageSummaryContent] weekly_pack/state snapshot error:", err);

      dashboardVisible.value = false;
    }
  );
}

/*
 * Keep the Print PDF button visible.
 *
 * Previously this depended upon:
 *
 * venues/{venueId}/weekly_pack/state.dashboardVisible
 *
 * which caused the button to disappear when the document
 * did not exist or could not be read.
 */
const canShowPrintButton = computed(() => {
  return true;
});

/* =========================================================
   NORMALISED STAFF
========================================================= */

const staffRowsNorm = computed(() =>
  Array.isArray(props.staffRows) ? props.staffRows : []
);

/* =========================================================
   WEEK DATES
========================================================= */

const weekEndingISO = computed(() => {
  const raw = String(props.weekEndingDate || "").trim();

  if (!raw) {
    return "";
  }

  if (isISODate(raw)) {
    return raw;
  }

  const d = new Date(raw);

  if (!Number.isNaN(d.getTime())) {
    return d.toISOString().slice(0, 10);
  }

  return "";
});

const weekDates = computed(() => {
  const fromWeekDays = (Array.isArray(props.weekDays) ? props.weekDays : [])
    .map((d) => String(d?.ymd || "").trim())
    .filter((ymd) => isISODate(ymd));

  if (fromWeekDays.length > 0) {
    return [...new Set(fromWeekDays)].sort();
  }

  if (!weekEndingISO.value) {
    return [];
  }

  const we = parseYmd(weekEndingISO.value);

  const mon = addDays(we, -6);

  return Array.from(
    {
      length: 7,
    },

    (_, i) => toYmd(addDays(mon, i))
  );
});

const selectedDateRange = computed(() => {
  const dates = weekDates.value;

  if (!dates.length) {
    return {
      from: "",
      to: "",
    };
  }

  return {
    from: dates[0],

    to: dates[dates.length - 1],
  };
});

/* =========================================================
   ACTUAL SALES
========================================================= */

const weeklyActualSales = computed(() =>
  tillRows.value.reduce(
    (sum, r) => sum + safeNum(r?.declared_total ?? r?.declaredTotal),

    0
  )
);

const actualSalesByDate = computed(() => {
  const map = new Map();

  for (const ymd of weekDates.value) {
    map.set(ymd, 0);
  }

  for (const r of tillRows.value || []) {
    const ymd = String(r?.trading_date ?? r?.tradingDate ?? r?.date ?? "").slice(0, 10);

    if (!ymd || !map.has(ymd)) {
      continue;
    }

    const current = safeNum(map.get(ymd));

    const add = safeNum(r?.declared_total ?? r?.declaredTotal);

    map.set(ymd, current + add);
  }

  return map;
});

async function loadActualSales() {
  const v = String(props.venueId || "").trim();

  const from = String(selectedDateRange.value.from || "").trim();

  const to = String(selectedDateRange.value.to || "").trim();

  if (!v || !from || !to) {
    tillRows.value = [];

    return;
  }

  loadingActualSales.value = true;

  try {
    const res = await getTillsForDateRange({
      venue_id: v,

      date_from: from,

      date_to: to,
    });

    tillRows.value = Array.isArray(res?.rows) ? res.rows : [];
  } catch (e) {
    error.value = e?.message || String(e);

    tillRows.value = [];
  } finally {
    loadingActualSales.value = false;
  }
}

/* =========================================================
   CLOCKED ACTUAL HOURS

   Reads:
   venues/{venueId}/time_clock_shifts

   Calculates:
   clockInRoundedAt -> clockOutRoundedAt only

   Displays:
   HH:mm in 15-minute blocks
========================================================= */

async function loadActualClockHours() {
  const v = String(props.venueId || "").trim();

  const from = String(selectedDateRange.value.from || "").trim();

  const to = String(selectedDateRange.value.to || "").trim();

  actualClockHoursMap.value = {};

  if (!v || !from || !to) {
    return;
  }

  loadingActualClockHours.value = true;

  try {
    const shiftsRef = collection(db, "venues", v, TIME_CLOCK_SUBCOLLECTION);

    const q = query(
      shiftsRef,

      where("businessDate", ">=", from),

      where("businessDate", "<=", to)
    );

    const snap = await getDocs(q);

    const totals = {};

    snap.forEach((docSnap) => {
      const data = docSnap.data() || {};

      const status = String(data?.status || "")
        .trim()
        .toLowerCase();

      if (status && status !== "closed") {
        return;
      }

      const staffId = clockShiftStaffId(data);

      if (!staffId) {
        return;
      }

      /*
       * Only rounded clock-in and rounded clock-out
       * are used.
       */
      if (!data?.clockInRoundedAt || !data?.clockOutRoundedAt) {
        return;
      }

      const hours = clockShiftHours(data);

      if (hours <= 0) {
        return;
      }

      totals[staffId] = safeNum(totals[staffId]) + hours;
    });

    const rounded = {};

    for (const [staffId, hours] of Object.entries(totals)) {
      const roundedMins = roundMinutesToNearest15(safeNum(hours) * 60);

      rounded[staffId] = roundedMins / 60;
    }

    actualClockHoursMap.value = rounded;
  } catch (e) {
    console.error("[WageSummaryContent] loadActualClockHours error:", e);

    error.value = e?.message || String(e);

    actualClockHoursMap.value = {};
  } finally {
    loadingActualClockHours.value = false;
  }
}

/* =========================================================
   MANAGERS CONTRACT
========================================================= */

async function loadManagersContract() {
  const v = String(props.venueId || "").trim();

  const we = String(props.weekEndingDate || "").trim();

  if (!v || !isISODate(we)) {
    managersContract.value = 0;

    return;
  }

  loadingContract.value = true;

  try {
    const snap = await getDoc(doc(db, "venues", v, "managers_contract", we));

    if (!snap.exists()) {
      managersContract.value = 0;

      return;
    }

    const data = snap.data() || {};

    managersContract.value = safeNum(data.managers_contract ?? data.value ?? 0);
  } catch (e) {
    error.value = e?.message || String(e);

    managersContract.value = 0;
  } finally {
    loadingContract.value = false;
  }
}

/* =========================================================
   SAVED ACTUAL HOURS
========================================================= */

async function loadSavedActualWages() {
  const v = String(props.venueId || "").trim();

  const we = String(props.weekEndingDate || "").trim();

  actualHoursInputMap.value = {};

  if (!v || !isISODate(we)) {
    actualHoursSavedMap.value = {};

    return;
  }

  try {
    const snap = await getDoc(doc(db, "venues", v, "wage_week_summaries", we));

    if (!snap.exists()) {
      actualHoursSavedMap.value = {};

      return;
    }

    const data = snap.data() || {};

    const savedMap =
      data?.actual_hours_by_staff &&
      typeof data.actual_hours_by_staff === "object" &&
      !Array.isArray(data.actual_hours_by_staff)
        ? data.actual_hours_by_staff
        : {};

    const normalized = {};

    for (const [staffId, hours] of Object.entries(savedMap)) {
      const n = Number(hours);

      if (Number.isFinite(n) && n >= 0) {
        const roundedMins = roundMinutesToNearest15(n * 60);

        normalized[String(staffId)] = roundedMins / 60;
      }
    }

    actualHoursSavedMap.value = normalized;
  } catch (e) {
    error.value = e?.message || String(e);

    actualHoursSavedMap.value = {};

    actualHoursInputMap.value = {};
  }
}

/* =========================================================
   LIVE DAILY ROWS
========================================================= */

const liveDailyRows = computed(() => {
  const days = Array.isArray(props.weekDays) ? props.weekDays : [];

  const rows = [];

  for (const d of days) {
    const ymd = String(d?.ymd || "").trim();

    if (!ymd) {
      continue;
    }

    const label = String(d?.label || "").trim() || ymd;

    let hours = 0;

    let wages = 0;

    for (const s of staffRowsNorm.value) {
      const staffId = staffIdOf(s);

      if (!staffId) {
        continue;
      }

      const rate = staffPayRate(s);

      const shifts = props.shiftsForCell(staffId, ymd) || [];

      for (const sh of shifts) {
        const h = shiftHoursFromObject(sh);

        hours += h;

        wages += h * rate;
      }
    }

    const forecast = safeNum(forecastByDate.value.get(ymd));

    const actualSales = safeNum(actualSalesByDate.value.get(ymd));

    rows.push({
      ymd,

      label,

      hours,

      wages,

      forecast,

      actualSales,

      forecastWagePct: forecast > 0 ? (wages / forecast) * 100 : 0,

      actualWagePct: actualSales > 0 ? (wages / actualSales) * 100 : 0,
    });
  }

  return rows;
});

/* =========================================================
   RESOLVED WEEK
========================================================= */

const hasResolvedWeekShape = computed(() => {
  return (
    !!String(props.venueId || "").trim() &&
    Array.isArray(props.weekDays) &&
    props.weekDays.length > 0 &&
    Array.isArray(staffRowsNorm.value) &&
    staffRowsNorm.value.length > 0
  );
});

/* =========================================================
   DATA LOAD WATCHER
========================================================= */

watch(
  () => [props.venueId, props.weekEndingDate, props.weekDays],

  async () => {
    await Promise.all([
      loadManagersContract(),

      loadSavedActualWages(),

      loadActualSales(),

      loadActualClockHours(),

      loadForecastWeek(),
    ]);
  },

  {
    immediate: true,

    deep: true,
  }
);

/* =========================================================
   WEEKLY PACK WATCHER
========================================================= */

watch(
  () => props.venueId,

  (v) => {
    attachWeeklyPackState(v);
  },

  {
    immediate: true,
  }
);

onMounted(() => {
  attachWeeklyPackState(props.venueId);
});

onUnmounted(() => {
  if (typeof weeklyPackUnsub === "function") {
    weeklyPackUnsub();
  }
});

/* =========================================================
   RESOLVED DAILY ROWS
========================================================= */

watch(
  [hasResolvedWeekShape, liveDailyRows],

  ([shapeReady, rows]) => {
    if (!shapeReady) {
      return;
    }

    resolvedDailyRows.value = Array.isArray(rows)
      ? rows.map((r) => ({
          ...r,
        }))
      : [];
  },

  {
    immediate: true,

    deep: true,
  }
);

const dailyRows = computed(() => resolvedDailyRows.value);

/* =========================================================
   WEEKLY HOURS / WAGES
========================================================= */

const liveWeeklyHours = computed(() =>
  dailyRows.value.reduce(
    (a, r) => a + safeNum(r.hours),

    0
  )
);

const liveWeeklyWagesFromRota = computed(() =>
  dailyRows.value.reduce(
    (a, r) => a + safeNum(r.wages),

    0
  )
);

watch(
  [hasResolvedWeekShape, liveWeeklyHours, liveWeeklyWagesFromRota],

  ([shapeReady, hours, wages]) => {
    if (!shapeReady) {
      return;
    }

    resolvedWeeklyHours.value = safeNum(hours);

    resolvedWeeklyRotaWages.value = safeNum(wages);
  },

  {
    immediate: true,
  }
);

const weeklyHours = computed(() => resolvedWeeklyHours.value);

const weeklyWagesFromRota = computed(() => resolvedWeeklyRotaWages.value);

/* =========================================================
   STAFF WEEKLY ROWS
========================================================= */

const liveStaffWeeklyRows = computed(() => {
  const days = Array.isArray(props.weekDays) ? props.weekDays : [];

  const out = [];

  for (const s of staffRowsNorm.value) {
    const staffId = staffIdOf(s);

    if (!staffId) {
      continue;
    }

    const rate = staffPayRate(s);

    let hours = 0;

    for (const d of days) {
      const ymd = String(d?.ymd || "").trim();

      if (!ymd) {
        continue;
      }

      const shifts = props.shiftsForCell(staffId, ymd) || [];

      for (const sh of shifts) {
        hours += shiftHoursFromObject(sh);
      }
    }

    out.push({
      staffId,

      name: staffName(s),

      jobTitle: staffJobTitle(s),

      payRate: rate,

      hours,

      wages: hours * rate,
    });
  }

  out.sort((a, b) => b.wages - a.wages);

  return out;
});

watch(
  [hasResolvedWeekShape, liveStaffWeeklyRows],

  ([shapeReady, rows]) => {
    if (!shapeReady) {
      return;
    }

    if (Array.isArray(rows) && rows.length > 0) {
      resolvedStaffWeeklyRows.value = rows.map((r) => ({
        ...r,
      }));
    }
  },

  {
    immediate: true,

    deep: true,
  }
);

const staffWeeklyRows = computed(() => {
  if (liveStaffWeeklyRows.value.length > 0) {
    return liveStaffWeeklyRows.value;
  }

  return resolvedStaffWeeklyRows.value;
});

/* =========================================================
   VALIDATION
========================================================= */

const canSaveActualWages = computed(() => {
  const v = String(props.venueId || "").trim();

  const we = String(props.weekEndingDate || "").trim();

  if (!v || !isISODate(we)) {
    return false;
  }

  for (const s of staffWeeklyRows.value) {
    const raw = actualHoursInputValue(s.staffId);

    if (raw === "" || raw == null) {
      continue;
    }

    if (!VALID_HHMM_RE.test(String(raw || "").trim())) {
      return false;
    }
  }

  return true;
});

/* =========================================================
   TOTALS
========================================================= */

const actualWages = computed(() =>
  staffWeeklyRows.value.reduce(
    (sum, row) => {
      return sum + actualWagesForStaff(row);
    },

    0
  )
);

const actualWagePct = computed(() =>
  weeklyActualSales.value > 0 ? (actualWages.value / weeklyActualSales.value) * 100 : 0
);

const forecastWagePct = computed(() =>
  weeklyForecast.value > 0 ? (weeklyWagesFromRota.value / weeklyForecast.value) * 100 : 0
);

const varianceWage = computed(() => actualWages.value - safeNum(managersContract.value));

const variancePct = computed(() => actualWagePct.value - forecastWagePct.value);

/* =========================================================
   PDF DATA
========================================================= */

function buildResolvedWageSummaryData() {
  return {
    venueId: asStr(props.venueId),

    weekEnding: asStr(weekEndingISO.value),

    weekRangeText: asStr(props.weekRangeText),

    weeklySummary: {
      totalHours: safeNum(weeklyHours.value),

      rotaWages: safeNum(weeklyWagesFromRota.value),

      actualWages: safeNum(actualWages.value),

      actualSales: safeNum(weeklyActualSales.value),

      forecastSales: safeNum(weeklyForecast.value),

      contractWages: safeNum(managersContract.value),

      varianceWage: safeNum(varianceWage.value),

      actualWagePct: safeNum(actualWagePct.value),

      forecastWagePct: safeNum(forecastWagePct.value),

      variancePct: safeNum(variancePct.value),
    },

    dailyRows: dailyRows.value.map((d) => ({
      ymd: asStr(d.ymd),

      label: asStr(d.label),

      hours: safeNum(d.hours),

      wages: safeNum(d.wages),

      forecast: safeNum(d.forecast),

      actualSales: safeNum(d.actualSales),

      forecastWagePct: safeNum(d.forecastWagePct),

      actualWagePct: safeNum(d.actualWagePct),
    })),

    staffWeeklyRows: staffWeeklyRows.value.map((s) => ({
      staffId: asStr(s.staffId),

      name: asStr(s.name),

      jobTitle: asStr(s.jobTitle),

      payRate: safeNum(s.payRate),

      hours: safeNum(s.hours),

      wages: safeNum(s.wages),

      actualHours: safeNum(actualHoursForStaffId(s.staffId)),

      actualHoursHHMM: actualHoursInputValue(s.staffId),

      actualWages: safeNum(actualWagesForStaff(s)),
    })),
  };
}

/* =========================================================
   PRINT PDF
========================================================= */

async function printWageSummaryPdf() {
  if (printingPdf.value) {
    return;
  }

  const v = asStr(props.venueId);

  const we = asStr(weekEndingISO.value);

  if (!v || !we) {
    showToast("Missing venue or week ending", "red");

    return;
  }

  printingPdf.value = true;

  try {
    const resolvedData = buildResolvedWageSummaryData();

    const fn = httpsCallable(fns(), "generateWageSummaryPdf");

    const res = await fn({
      venueId: v,

      weekEnding: we,

      resolvedData,
    });

    const url = extractUrl(res?.data);

    if (!url) {
      throw new Error("Wage summary PDF generated but no PDF URL was returned.");
    }

    window.open(url, "_blank", "noopener,noreferrer");

    showToast("Wage summary PDF generated.", "green");
  } catch (e) {
    console.error("[PRINT WAGE SUMMARY PDF]", e);

    showToast(e?.message || String(e), "red");
  } finally {
    printingPdf.value = false;
  }
}

/* =========================================================
   FORECAST SALES
========================================================= */

async function loadForecastWeek() {
  const v = String(props.venueId || "").trim();

  const we = String(props.weekEndingDate || "").trim();

  if (!v || !isISODate(we)) {
    forecastByDate.value = new Map();

    weeklyForecast.value = 0;

    return;
  }

  loadingForecast.value = true;

  error.value = "";

  try {
    const map = new Map();

    for (const d of props.weekDays || []) {
      const ymd = String(d?.ymd || "").trim();

      if (ymd) {
        map.set(ymd, 0);
      }
    }

    let loadedFromCells = false;

    try {
      const cellsRef = collection(db, "venues", v, "forecast_weeks", we, "cells");

      const cellsSnap = await getDocs(
        query(
          cellsRef,

          orderBy("trading_date", "asc")
        )
      );

      if (!cellsSnap.empty) {
        loadedFromCells = true;

        cellsSnap.forEach((docSnap) => {
          const data = docSnap.data() || {};

          const ymd = readTradingDate(data);

          if (!ymd) {
            return;
          }

          const current = safeNum(map.get(ymd));

          const add = firstNumber(data?.value, data?.amount, data?.forecast, data?.total);

          map.set(ymd, current + add);
        });
      }
    } catch (_) {
      /*
       * Ignore legacy shape fallback.
       */
    }

    if (!loadedFromCells) {
      const snap = await getDoc(doc(db, "venues", v, "forecast_weeks", we));

      if (snap.exists()) {
        const data = snap.data() || {};

        if (Array.isArray(data?.days)) {
          for (const day of data.days) {
            const ymd = readTradingDate(day);

            if (!ymd) {
              continue;
            }

            const current = safeNum(map.get(ymd));

            const add = firstNumber(
              day?.value,
              day?.amount,
              day?.forecast,
              day?.forecast_total,
              day?.total
            );

            map.set(ymd, current + add);
          }
        } else if (
          data?.cells &&
          typeof data.cells === "object" &&
          !Array.isArray(data.cells)
        ) {
          for (const cell of Object.values(data.cells)) {
            const ymd = readTradingDate(cell);

            if (!ymd) {
              continue;
            }

            const current = safeNum(map.get(ymd));

            const add = firstNumber(
              cell?.value,
              cell?.amount,
              cell?.forecast,
              cell?.forecast_total,
              cell?.total
            );

            map.set(ymd, current + add);
          }
        } else if (data?.payload?.cells && typeof data.payload.cells === "object") {
          for (const cell of Object.values(data.payload.cells)) {
            const ymd = readTradingDate(cell);

            if (!ymd) {
              continue;
            }

            const current = safeNum(map.get(ymd));

            const add = firstNumber(
              cell?.value,
              cell?.amount,
              cell?.forecast,
              cell?.forecast_total,
              cell?.total
            );

            map.set(ymd, current + add);
          }
        }
      }
    }

    forecastByDate.value = map;

    weeklyForecast.value = Array.from(map.values()).reduce(
      (sum, value) => sum + safeNum(value),

      0
    );
  } catch (e) {
    error.value = e?.message || String(e);

    forecastByDate.value = new Map();

    weeklyForecast.value = 0;
  } finally {
    loadingForecast.value = false;
  }
}

/* =========================================================
   SAVE ACTUAL WAGES
========================================================= */

async function saveActualWages() {
  const v = String(props.venueId || "").trim();

  const we = String(props.weekEndingDate || "").trim();

  if (!v || !isISODate(we)) {
    showToast("Missing venue or week ending", "red");

    return;
  }

  savingActualWages.value = true;

  error.value = "";

  try {
    const user = auth?.currentUser || null;

    const userName =
      String(user?.displayName || "").trim() ||
      String(user?.email || "").trim() ||
      "Unknown user";

    const actualHoursByStaff = {};

    const actualHoursHHMMByStaff = {};

    const actualWagesByStaff = {};

    for (const row of staffWeeklyRows.value) {
      const hours = actualHoursForStaffId(row.staffId);

      const roundedMins = roundMinutesToNearest15(hours * 60);

      const roundedHours = roundedMins / 60;

      const wages = safeNum(row.payRate) * roundedHours;

      actualHoursByStaff[row.staffId] = roundedHours;

      actualHoursHHMMByStaff[row.staffId] = decimalHoursToHHMM(roundedHours);

      actualWagesByStaff[row.staffId] = wages;
    }

    await setDoc(
      doc(db, "venues", v, "wage_week_summaries", we),

      {
        venue_id: v,

        week_ending_date: we,

        actual_hours_by_staff: actualHoursByStaff,

        actual_hours_hhmm_by_staff: actualHoursHHMMByStaff,

        actual_wages_by_staff: actualWagesByStaff,

        actual_wages: actualWages.value,

        rota_wages: weeklyWagesFromRota.value,

        actual_sales: weeklyActualSales.value,

        forecast_sales: weeklyForecast.value,

        managers_contract: managersContract.value,

        updated_by_uid: user?.uid || "",

        updated_by_name: userName,

        updated_at: serverTimestamp(),
      },

      {
        merge: true,
      }
    );

    actualHoursSavedMap.value = {
      ...actualHoursByStaff,
    };

    const refreshedInputs = {};

    for (const [staffId, hours] of Object.entries(actualHoursByStaff)) {
      refreshedInputs[staffId] = decimalHoursToHHMM(hours);
    }

    actualHoursInputMap.value = refreshedInputs;

    showToast("Actual hours saved", "green");
  } catch (e) {
    error.value = e?.message || String(e);

    showToast("Failed to save actual hours", "red");
  } finally {
    savingActualWages.value = false;
  }
}
</script>

<style scoped>
.wage-summary-root {
  width: 100%;
  min-width: 0;
}

.actual-hours-header {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
}

.actual-hours-input-wrap {
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.actual-hours-input {
  width: 110px;
  margin: 0;
  text-align: right;
}

.hover-tooltip-wrap {
  position: relative;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  line-height: 1;
}

.hover-tooltip-icon {
  cursor: help;
  color: #546e7a;
  user-select: none;
}

.hover-tooltip-bubble {
  position: absolute;

  left: 50%;
  bottom: calc(100% + 10px);

  transform: translateX(-50%) translateY(4px);

  min-width: 190px;
  max-width: 260px;

  padding: 8px 10px;

  border-radius: 8px;

  background: #263238;
  color: #ffffff;

  font-size: 12px;
  font-weight: 500;
  line-height: 1.35;

  text-align: center;
  white-space: normal;

  opacity: 0;
  visibility: hidden;
  pointer-events: none;

  z-index: 1000;

  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.28);

  transition: opacity 0.16s ease, transform 0.16s ease, visibility 0.16s ease;
}

.hover-tooltip-bubble::after {
  content: "";

  position: absolute;

  top: 100%;
  left: 50%;

  transform: translateX(-50%);

  border-width: 6px;
  border-style: solid;

  border-color: #263238 transparent transparent transparent;
}

.hover-tooltip-wrap:hover .hover-tooltip-bubble,
.hover-tooltip-wrap:focus-within .hover-tooltip-bubble {
  opacity: 1;
  visibility: visible;

  transform: translateX(-50%) translateY(0);
}

@media (max-width: 600px) {
  .actual-hours-header {
    justify-content: flex-start;
  }

  .actual-hours-input-wrap {
    justify-content: flex-start;
  }

  .hover-tooltip-bubble {
    left: auto;
    right: 0;

    transform: translateY(4px);
  }

  .hover-tooltip-wrap:hover .hover-tooltip-bubble,
  .hover-tooltip-wrap:focus-within .hover-tooltip-bubble {
    transform: translateY(0);
  }

  .hover-tooltip-bubble::after {
    left: auto;
    right: 10px;

    transform: none;
  }
}
</style>
